import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';
import { defaultMaxDate, defaultToday, PRICELINE_DATE_FORMAT } from '../constants/dates';
import getInitialHoveredDate from '../utils/getInitialHoveredDate';
import keyNavHandler from '../utils/keyNavHandler';
function useCalendarForm({
  allowSameDay = false,
  didUseKeyNavigation: initialDidUseKeyNavigation = false,
  endDate: initialEndDate = null,
  isFlightCalendar,
  maxDate = defaultMaxDate,
  maxDaysToSelect,
  maxSelectableDate: initialMaxSelectableDate = defaultMaxDate,
  minDate = defaultToday,
  scrollDate = 0,
  selectTwoDates = true,
  startDate: initialStartDate = null,
  onChange = () => {},
  onDismiss = () => {},
  setCalendarRef,
  setCalenderRef
}) {
  const initialHoveredDate = useMemo(() => getInitialHoveredDate({
    endDate: initialEndDate,
    minDate: minDate,
    selectTwoDates: selectTwoDates,
    startDate: initialStartDate
  }), []);
  const calendarRef = useRef(null);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [hoveredDate, setHoveredDate] = useState(initialHoveredDate);
  const [maxSelectableDate, setMaxSelectableDate] = useState(maxDate || initialMaxSelectableDate);
  const [didUseKeyNavigation, setDidUseKeyNavigation] = useState(initialDidUseKeyNavigation);
  const [tabClose, setTabClose] = useState(false);
  useEffect(() => {
    const callback = setCalendarRef || setCalenderRef;
    callback?.(calendarRef.current);
  }, [calendarRef]);
  const calculateMaxSelectableDate = useCallback(date => {
    let newMaxSelectableDate;
    if (maxDaysToSelect > 1) {
      newMaxSelectableDate = format(addDays(parseISO(date), maxDaysToSelect - 2), PRICELINE_DATE_FORMAT);
    } else if (isValid(date) && date instanceof Date && !isFlightCalendar) {
      // 30 as a default value
      newMaxSelectableDate = format(addDays(date, 30), PRICELINE_DATE_FORMAT);
    } else {
      newMaxSelectableDate = maxDate || defaultMaxDate;
    }
    setMaxSelectableDate(newMaxSelectableDate);
  }, [isFlightCalendar, maxDate, maxDaysToSelect, maxSelectableDate]);
  const handleKeyDismiss = useCallback(evt => {
    if (evt.keyCode === 9 && tabClose) {
      onDismiss(evt);
    } else if (evt.keyCode === 27) {
      evt.preventDefault();
      evt.stopPropagation();
      onDismiss(evt);
    }
  }, [tabClose]);
  const onDayClicked = useCallback((event, date) => {
    if (selectTwoDates) {
      const isInitialClick = Boolean(startDate && endDate || !startDate || date < startDate);
      const isEndDateClick = startDate && Boolean(allowSameDay ? date >= startDate : date > startDate);
      if (isInitialClick) {
        calculateMaxSelectableDate(date);
        setStartDate(date);
        setEndDate(null);
        setHoveredDate(date);
        onChange(event, {
          startDate: date,
          endDate: null
        });
      } else if (isEndDateClick) {
        calculateMaxSelectableDate(startDate);
        setEndDate(date);
        onChange(event, {
          startDate,
          endDate: date
        });
      }
    } else {
      onChange(event, {
        startDate: date
      });
      setStartDate(date);
      setEndDate(null);
      calculateMaxSelectableDate(startDate);
    }
  }, [startDate, endDate, allowSameDay, calculateMaxSelectableDate, onChange, selectTwoDates]);
  const onDayHovered = useCallback((event, hoveredDate, keyNav) => {
    if (endDate && !keyNav) {
      return;
    }
    const isSingleDateSelected = !selectTwoDates && startDate;
    const isBothDatesSelected = selectTwoDates && startDate && endDate;
    if (keyNav && (isSingleDateSelected || isBothDatesSelected)) {
      setStartDate(0);
      setEndDate(0);
    }
    if (!didUseKeyNavigation && keyNav) {
      setDidUseKeyNavigation(true);
    }
    setHoveredDate(hoveredDate);
  }, [didUseKeyNavigation, endDate, selectTwoDates, startDate]);
  const handleKeyNav = useCallback(event => {
    // Only preventDefault if it's an action that we've accounted for (e.g. don't prevent tabbing away)
    const {
      action,
      date
    } = keyNavHandler(event, hoveredDate, maxSelectableDate, minDate);
    if (action) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (date && action === 'NAVIGATE') {
      onDayHovered(event, date, true);
    } else if (date && action === 'SELECT') {
      onDayClicked(event, date);
    }
  }, [hoveredDate, maxSelectableDate, minDate, onDayClicked, onDayHovered]);
  useEffect(() => {
    if (startDate && !endDate) {
      calculateMaxSelectableDate(startDate);
    }
  }, []);
  return {
    allowSameDay,
    calendarRef,
    didUseKeyNavigation,
    endDate,
    hoveredDate,
    isFlightCalendar,
    maxDate,
    maxDaysToSelect,
    maxSelectableDate,
    minDate,
    scrollDate,
    selectTwoDates,
    startDate,
    tabClose,
    handleKeyDismiss,
    handleKeyNav,
    onDayClicked,
    onDayHovered,
    setTabClose
  };
}
export default useCalendarForm;